<template>
  <v-carousel
    :height="height"
    :model-value="modelValue"
    hide-delimiter-background
    hide-delimiters
    :touch="touchHandlers"
    :show-arrows="documents.length > 1 ? 'hover' : false"
    color="surface"
    class="flex-grow-1 flex-shrink-1 crossfade-carousel"
    :class="$vuetify.theme.current.dark ? 'bg-grey' : 'bg-grey-lighten-3'"
    @update:model-value="($event) => emit('update:modelValue', $event)"
  >
    <v-carousel-item
      v-for="(document, index) in documents"
      :key="document.meta._id"
      :value="index"
      reverse-transition="crossfade"
      transition="crossfade"
    >
      <v-card :loading="pending" class="rounded-0" variant="flat">
        <v-card-item class="py-2 pl-4 pr-2" :title="`${document.meta.description}.${document.meta.extension}`">
          <template #prepend>
            <span v-if="documents.length" style="min-width: 40px" class="text-no-wrap mr-3"
              >{{ currentIndex + 1 }} / {{ documents.length }}</span
            >
          </template>
          <template #subtitle>
            <div class="d-flex align-center" style="gap: 8px">
              <div class="text-no-wrap">
                <span>{{ $d(new Date(document.meta.createdAt!)) }}</span>
              </div>
              <template v-if="document.meta.remark">
                <v-divider vertical />
                <div class="ellipsis">{{ document.meta.remark }}</div>
              </template>

              <v-tooltip v-if="document.meta.private" :text="$t('foreclosed')" location="top">
                <template #activator="{ props: tooltipProps }">
                  <v-icon v-bind="tooltipProps" icon="lock" size="small" style="margin-bottom: 2px" />
                </template>
              </v-tooltip>

              <v-tooltip v-if="isPrimary" :text="$t('primaryImage')" location="top">
                <template #activator="{ props: tooltipProps }">
                  <v-icon v-bind="tooltipProps" icon="star" color="yellow-darken-2" size="small" style="margin-bottom: 1px" />
                </template>
              </v-tooltip>
            </div>
          </template>

          <template #append>
            <v-tooltip v-if="readonly" location="bottom">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  icon="visibility"
                  variant="text"
                  size="small"
                  target="_blank"
                  :href="`/files/${document.meta._id}`"
                />
              </template>

              <template #default>{{ $t("viewInBrowser") }}</template>
            </v-tooltip>

            <div v-if="!readonly" class="d-flex" style="gap: 8px">
              <file-actions
                v-model="document.meta"
                :hide-rotate="readonly"
                :is-primary="isPrimary"
                :show-defaulting-options="!hidePrimarySettings"
                @delete="removeFile(document.meta._id)"
                @update:model-value="(fileData) => emit('update-document', fileData)"
                @set-primary="
                  () => {
                    emit('update-document', document.meta)
                    emit('setPrimary')
                  }
                "
              />
            </div>

            <v-tooltip v-else location="bottom">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  variant="text"
                  icon
                  size="small"
                  @click="downloadFile(document.meta._id, document.meta.description, document.meta.extension)"
                >
                  <v-icon icon="download" size="large" />
                </v-btn>
              </template>

              <template #default>{{ $t("download") }}</template>
            </v-tooltip>

            <v-tooltip location="bottom">
              <template #activator="{ props: tooltipProps }">
                <v-btn v-bind="tooltipProps" variant="text" icon="close" :aria-label="$t('exitFullscreen')" @click="() => emit('close')" />
              </template>

              <template #default>{{ $t("exitFullscreen") }}</template>
            </v-tooltip>
          </template>
        </v-card-item>
      </v-card>

      <image-zoom
        v-if="canPreviewAsImage(document.meta)"
        :reset-key="currentIndex"
        @zoom-change="(isZoomed) => onZoomChange(index, isZoomed)"
      >
        <v-img
          height="100%"
          class="document-carousel-item"
          :src="document.src ?? document.thumbnailSrc"
          :style="`--angle: ${document.meta.angle ?? 0}deg;`"
          :cover="cover"
        />
      </image-zoom>

      <iframe
        v-else-if="isPdf(document.meta) && trustedPdfSources.get(document.meta._id)"
        class="file-carousel-frame"
        :src="trustedPdfSources.get(document.meta._id)"
        :title="`${document.meta.description}.${document.meta.extension}`"
      />

      <video v-else-if="isVideo(document.meta) && document.src" class="file-carousel-video" controls :src="document.src" />

      <glb-model-viewer v-else-if="isGlbModel(document.meta) && document.src" :document="document" />

      <div v-else class="file-carousel-preview d-flex align-center justify-center pa-4">
        <v-card variant="tonal" max-width="420" class="w-100 text-center">
          <v-card-text class="py-10 d-flex flex-column align-center" style="gap: 12px">
            <img v-if="getFileIconSrc(document.meta)" :src="getFileIconSrc(document.meta)" style="max-height: 64px" />
            <div class="text-headline-large text-uppercase">{{ document.meta.extension }}</div>
            <div class="text-body-large text-medium-emphasis">{{ document.meta.description }}</div>

            <div class="d-flex" style="gap: 8px">
              <v-btn prepend-icon="visibility" target="_blank" :href="`/files/${document.meta._id}`">
                {{ $t("viewInBrowser") }}
              </v-btn>

              <v-btn prepend-icon="download" @click="downloadFile(document.meta._id, document.meta.description, document.meta.extension)">
                {{ $t("download") }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </v-carousel-item>

    <inner-loading :label="$t('loadingImages')" />

    <div v-if="documents.length === 0 && !pending" class="h-100 w-100 d-flex flex-column justify-center align-center">
      <v-icon size="80" icon="image" />
      <p>{{ $t("noImages") }}</p>

      <v-dialog v-if="!readonly && organizationId && assetId" persistent width="600">
        <template #activator="{ props: dialogProps }">
          <v-btn variant="tonal" v-bind="dialogProps" :disabled="!hasScope(organizationId, AuthScope.CAN_CREATE_FILES)">{{
            $t("addImages")
          }}</v-btn>
        </template>

        <template #default="{ isActive }">
          <v-card :title="$t('addImages')">
            <add-file-form
              accept="image/*"
              :create-function="(files: Array<File>) => assetStore.addImages(assetId!, files)"
              @ok="
                () => {
                  emit('update:modelValue', 0)
                  isActive.value = false
                }
              "
              @cancel="() => (isActive.value = false)"
            />
          </v-card>
        </template>
      </v-dialog>
    </div>
  </v-carousel>

  <v-divider />

  <v-card-actions v-if="showThumbnails" class="justify-center pa-0">
    <div class="carousel-wrapper">
      <v-btn v-if="documents.length > 1" icon="chevron_left" @click="scrollLeft" />

      <div ref="viewport" class="carousel-viewport">
        <div class="carousel-thumbnails">
          <button
            v-for="(document, index) in documents"
            :key="document.meta._id"
            class="carousel-thumbnail"
            :class="{ 'is-active': currentIndex === index }"
            @click="select(index)"
          >
            <v-img v-if="canPreviewAsImage(document.meta)" :src="document.thumbnailSrc ?? document.src" cover height="50" width="50" />

            <img v-else-if="getFileIconSrc(document.meta)" :src="getFileIconSrc(document.meta)" width="60" style="object-fit: contain" />

            <div
              v-else
              class="carousel-thumbnail-file"
              :style="{ color: `rgb(var(--v-theme-${extensionColor(document.meta.extension)}))` }"
            >
              {{ document.meta.extension }}
            </div>
          </button>
        </div>
      </div>

      <v-btn v-if="documents.length > 1" icon="chevron_right" @click="scrollRight" />
    </div>
  </v-card-actions>
</template>

<script setup lang="ts">
interface AssetDocument {
  src?: string
  thumbnailSrc?: string
  meta: FileData
}
const $i18n = useI18n()

const emit = defineEmits<{
  (e: "update:modelValue", value: number): void
  (e: "remove-document", fileId: string): void
  (e: "update-document", fileData: FileData): void
  (e: "setPrimary" | "close"): void
}>()

const props = defineProps<{
  modelValue?: number
  assetId?: string
  organizationId?: string
  documents: Array<AssetDocument>
  pending?: boolean
  height?: string
  cover?: boolean
  readonly?: boolean
  isPrimary?: boolean
  showThumbnails?: boolean
  hidePrimarySettings?: boolean
}>()
const { modelValue, assetId, organizationId, documents, pending, height, cover, readonly, isPrimary, showThumbnails, hidePrimarySettings } =
  toRefs(props)

const assetStore = useAssetStore()
const { hasScope } = useAuthStore()
const mainStore = useMainStore()
const fileStore = useFileStore()

const { getFileIconSrc, canPreviewAsImage } = useFileExtensions()

const downloadFile = useDownloadFile(mainStore.notify, $i18n, fileStore)

const VIDEO_EXTENSIONS = ["mp4", "mov", "webm", "avi", "mkv", "ogg"] as const
const isPdf = (file: FileData) => file.extension === "pdf"
const isGlbModel = (file: FileData) => file.extension === "glb"
const isVideo = (file: FileData) => VIDEO_EXTENSIONS.includes(file.extension as (typeof VIDEO_EXTENSIONS)[number])

const removeFile = async (fileId: string) => {
  emit("update:modelValue", 0)
  emit("remove-document", fileId)
}

const viewport = ref<HTMLElement | null>(null)
const currentIndex = computed(() => modelValue.value ?? 0)
const isTouchSlideLocked = ref(false)
const trustedPdfSources = ref<Map<string, string>>(new Map())

const revokeTrustedPdfSources = () => {
  trustedPdfSources.value.forEach((url) => URL.revokeObjectURL(url))
  trustedPdfSources.value.clear()
}

const isTrustedPdfBlob = async (blob: Blob, contentType?: string | null) => {
  const normalizedContentType = contentType?.toLowerCase() ?? blob.type.toLowerCase()
  const isPdfMime = normalizedContentType.includes("application/pdf")
  const signature = await blob.slice(0, 5).text()
  return isPdfMime && signature === "%PDF-"
}

const buildTrustedPdfSources = async () => {
  revokeTrustedPdfSources()

  const pdfDocuments = documents.value.filter((document) => isPdf(document.meta) && !!document.src)
  const entries = await Promise.all(
    pdfDocuments.map(async (document) => {
      try {
        const response = await fetch(document.src!)
        if (!response.ok) return

        const contentType = response.headers.get("content-type")
        const blob = await response.blob()
        const isTrusted = await isTrustedPdfBlob(blob, contentType)
        if (!isTrusted) return

        return [document.meta._id, URL.createObjectURL(blob)] as const
      } catch {
        return
      }
    }),
  )

  trustedPdfSources.value = new Map(entries.filter((entry): entry is readonly [string, string] => !!entry))
}

const onZoomChange = (index: number, isZoomed: boolean) => {
  if (index !== currentIndex.value) return
  isTouchSlideLocked.value = isZoomed
}

const onSwipeLeft = () => {
  if (!isTouchSlideLocked.value) scrollRight()
}

const onSwipeRight = () => {
  if (!isTouchSlideLocked.value) scrollLeft()
}

const touchHandlers = {
  left: onSwipeLeft,
  right: onSwipeRight,
}

const scrollLeft = () => {
  const newIndex = currentIndex.value === 0 ? documents.value.length - 1 : currentIndex.value - 1

  emit("update:modelValue", newIndex)
}

const scrollRight = () => {
  const newIndex = currentIndex.value === documents.value.length - 1 ? 0 : currentIndex.value + 1

  emit("update:modelValue", newIndex)
}

const scrollToIndex = (index: number) => {
  const el = viewport.value?.children[0]?.children[index] as HTMLElement
  el?.scrollIntoView({
    behavior: "smooth",
    inline: "center",
    block: "nearest",
  })
}

const select = (index: number) => {
  emit("update:modelValue", index)
  scrollToIndex(index)
}

watch(currentIndex, (index) => {
  isTouchSlideLocked.value = false
  nextTick(() => scrollToIndex(index))
})

watch(
  documents,
  () => {
    void buildTrustedPdfSources()
  },
  { immediate: true, deep: true },
)

onBeforeUnmount(() => {
  revokeTrustedPdfSources()
})
</script>

<style lang="scss">
@use "sass:map";
@use "vuetify/lib/styles/settings/variables" as *;

/* Carousel custom styles */
.carousel-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
}

.carousel-viewport {
  max-width: 600px;
  overflow: hidden;

  @media #{map.get($display-breakpoints, 'xs')} {
    max-width: 300px;
  }
}

.carousel-thumbnails {
  display: flex;
  bottom: 8px;
  gap: 6px;
  z-index: 1;
  overflow-x: auto;
  padding: 4px;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.carousel-thumbnail {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 5px;
  overflow: hidden;
  opacity: 0.5;
  filter: grayscale(100%);
  transition:
    opacity 0.2s,
    filter 0.2s,
    border-color 0.2s;

  &.is-active {
    opacity: 1;
    filter: none;
    border-color: white;
  }

  &:hover:not(.is-active) {
    opacity: 0.75;
  }
}

.carousel-thumbnail-file {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: rgb(var(--v-theme-surface));
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.file-carousel-frame,
.file-carousel-preview {
  width: 100%;
  height: 100%;
  border: 0;
}

.file-carousel-video {
  display: block;
  width: 100%;
  height: calc(100% - 60px);
  margin: 0 auto;
  background: black;
  object-fit: contain;
}

.document-carousel-item {
  // dynamically updated by template style binding
  --angle: 0deg;

  .v-img__img.v-img__img--contain {
    transform: rotate(var(--angle));
  }
}

// TODO: Can be replaced with vuetify built-in crossfade transition planned for 3.10
// https://vuetifyjs.com/en/components/carousels/#custom-transition
.crossfade-carousel > .v-window__container {
  isolation: isolate;
  > .v-window-item {
    mix-blend-mode: plus-lighter;
  }
}

.crossfade-enter-active,
.crossfade-leave-active {
  transition: all 0.7s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.crossfade-leave-from,
.crossfade-leave-to {
  position: absolute !important;
  top: 0;
  width: 100%;
}

.crossfade-enter-from,
.crossfade-leave-to {
  opacity: 0;
}
</style>
